import type {
  Endpoints,
  NotificationsResponse,
  CustomNotification,
} from '~/types'

export const useNotificationsStore = defineStore('notificationsStore', () => {
  const notifications = ref<CustomNotification[]>([])
  const more = ref(false)
  const unread = ref<number>()
  const lastId = ref<number>()
  // bumped by every answer that sets the count: a count read before a newer one is dropped (the read of the
  // count after a reward, PR #1264, may come back after the reward modal marked its notifications read)
  let countVersion = 0
  const setUnreadCount = (count: number | undefined) => {
    countVersion++
    unread.value = count
  }

  const initStore = ({
    results,
    has_more,
    unread_count,
  }: NotificationsResponse) => {
    notifications.value = results
    lastId.value = results[results.length - 1].id
    more.value = has_more
    setUnreadCount(unread_count)
  }

  const setNotifications = ({
    results,
    unread_count,
  }: NotificationsResponse) => {
    notifications.value = [
      ...results.filter((n) => !notifications.value.find((s) => s.id === n.id)),
      ...notifications.value,
    ]
    setUnreadCount(unread_count)
  }

  const addNotifications = ({
    results,
    has_more,
    unread_count,
  }: NotificationsResponse) => {
    if (results.length) {
      notifications.value.push(...results)
      lastId.value = results[results.length - 1].id
    }

    more.value = has_more
    setUnreadCount(unread_count)
  }

  const setCount = ({ unread_count }: NotificationsResponse) => {
    setUnreadCount(unread_count)
  }

  const setUnread = (
    notification: CustomNotification,
    {
      unread_count,
    }: NotificationsResponse | Endpoints['PUT__NOTIFICATION_READ'],
  ) => {
    setUnreadCount(unread_count)
    notifications.value = notifications.value.map((n) => {
      if (n.id === notification.id) {
        return {
          ...n,
          read_at: new Date().toISOString(),
        }
      }
      return n
    })
  }

  const setUnreadAll = ({
    unread_count,
  }: Endpoints['PUT__NOTIFICATION_READ_ALL']) => {
    setUnreadCount(unread_count)
    notifications.value = notifications.value.map((n) => {
      if (!n.read_at) {
        return {
          ...n,
          read_at: new Date().toISOString(),
        }
      }

      return n
    })
  }

  const fetchNotifications = async () => {
    const { data, error } = await useCustomFetch<
      Endpoints['GET__NOTIFICATION_LAST']
    >(API.GET__NOTIFICATION_LAST)

    const rawData = unref(data)

    if (!error.value && rawData) {
      return initStore(rawData)
    }
    logError(
      'from actions',
      'There was a problem fetching notifications: ',
      error,
    )
  }

  const { authenticated } = useAuthenticated()

  const fetchUnreadCount = async () => {
    if (!authenticated.value) return
    const version = countVersion
    const { data, error } = await useCustomFetch<
      Endpoints['GET__NOTIFICATION_COUNT']
    >(API.GET__NOTIFICATION_COUNT)

    const rawData = unref(data)
    if (!error.value && rawData) {
      // another answer set the count meanwhile: it is the newer one
      if (version !== countVersion) return
      return setCount(rawData)
    }

    logError(
      'from actions',
      'There was a problem fetching unread count notifications: ',
      error,
    )
  }

  const loadNewerNotifications = async () => {
    if (!notifications.value.length) {
      await fetchNotifications()
      return
    }
    const { data, error } = await useCustomFetch<
      Endpoints['GET__NOTIFICATION_LAST']
    >(API.GET__NOTIFICATION_LAST)

    const rawData = unref(data)

    if (!error.value && rawData) {
      return setNotifications(rawData)
    }

    logError(
      'from actions',
      'There was a problem fetching newer notifications: ',
      error,
    )
  }

  const loadMoreNotifications = async () => {
    const { data, error } = await useCustomFetch<
      Endpoints['GET__NOTIFICATION_LAST']
    >(API.GET__NOTIFICATION_LAST, {
      query: {
        id: lastId.value,
      },
    })

    const rawData = unref(data)

    if (!error.value && rawData) {
      return addNotifications(rawData)
    }

    logError(
      'from actions',
      'There was a problem fetching more notifications: ',
      error,
    )
  }

  const readNotification = async (notification: CustomNotification) => {
    if (notification.read_at === null) {
      const { data, error } = await useCustomFetch<
        Endpoints['PUT__NOTIFICATION_READ']
      >(API.PUT__NOTIFICATION_READ.replace('$id', notification.id.toString()), {
        method: 'PUT',
      })

      const rawData = unref(data)

      if (!error.value && rawData) {
        return setUnread(notification, rawData)
      }

      logError(
        'from actions',
        'There was a problem reading notification: ',
        error,
      )
    }
  }

  /**
   * The reward modal shows badges just won: their notifications are read (the bell stops counting them, the list
   * shows them read). `userBadges` are the user_badge_id of badges_reward.
   */
  const readBadgeNotifications = async (userBadges: number[]) => {
    if (!authenticated.value || !userBadges.length) return
    const { data, error } = await useCustomFetch<
      Endpoints['PUT__NOTIFICATION_READ_BADGES']
    >(API.PUT__NOTIFICATION_READ_BADGES, {
      method: 'PUT',
      body: { user_badges: userBadges },
    })

    const rawData = unref(data)
    if (!error.value && rawData) {
      setUnreadCount(rawData.unread_count)
      const readAt = new Date().toISOString()
      notifications.value = notifications.value.map((n) =>
        rawData.read.includes(n.id) && !n.read_at
          ? { ...n, read_at: readAt }
          : n,
      )
      return
    }

    logError(
      'from actions',
      'There was a problem reading badge notifications: ',
      error,
    )
  }

  const readAllNotifications = async () => {
    const { data, error } = await useCustomFetch<
      Endpoints['PUT__NOTIFICATION_READ_ALL']
    >(API.PUT__NOTIFICATION_READ_ALL, { method: 'PUT' })

    const rawData = unref(data)

    if (!error.value && rawData) {
      return setUnreadAll(rawData)
    }

    logError(
      'from actions',
      'There was a problem reading all notifications: ',
      error,
    )
  }

  return {
    // STATE
    notifications,
    more,
    unread,
    lastId,

    // ACTIONS
    fetchNotifications,
    fetchUnreadCount,
    loadNewerNotifications,
    loadMoreNotifications,
    readNotification,
    readBadgeNotifications,
    readAllNotifications,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useNotificationsStore, import.meta.hot),
  )
}
